// src/b2b/components/PriceCalendarModal.jsx
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function PriceCalendarModal({
  productCode, // ex: "HOTTNDJE005931-367"
  defaultFrom, // ex: "LT_MRS" ou "LYS" (code)
  isOpen,
  nights, // ex: 3, 5, 7...
  onClose,
  onSelect,
  minDate, // ({ dateISO, price }) => void
}) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  // charge /api/prices?code=...&from=...&nights=...
  useEffect(() => {
    if (!isOpen) return;
    // on exige code + from + nights pour avoir les bons prix
    if (!productCode || !defaultFrom || !nights) {
      setErr('Paramètres incomplets (code/from/nights).');
      setData(null);
      return;
    }

    // borne basse = minDate (si fourni) sinon aujourd’hui à minuit
    const min = new Date();
    min.setHours(0, 0, 0, 0);

    const controller = new AbortController();
    setLoading(true);
    setErr(null);

    axios
      .get(`/api/prices`, {
        params: { code: productCode, from: defaultFrom, nights: Number(nights) },
        signal: controller.signal,
        timeout: 15000,
      })
      .then((res) => {
        // sécurité: tri par date croissante
        const payload = res.data || {};
        console.log(
          '%csrc\b2bcomponentsPriceCalendarModal.jsx:55 payload',
          'color: #007acc;',
          payload,
        );
        if (Array.isArray(payload.prices)) {
          payload.prices = payload.prices
            .filter((p) => p?.date && (p?.price != null || p?.value != null))
            // ⚠️ garder seulement les dates STRICTEMENT > min
            .filter((p) => {
              const d = new Date(p.date);
              d.setHours(0, 0, 0, 0);
              return d.getTime() >= min.getTime();
            })
            // tri croissant
            .sort((a, b) => new Date(a.date) - new Date(b.date));
        }
        setData(payload);
      })
      .catch((e) => {
        if (axios.isCancel(e) || e.name === 'CanceledError') return;
        setErr(e?.response?.data?.message || e.message || 'Erreur chargement');
        setData(null);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [isOpen, productCode, defaultFrom, nights]);

  // groupage par mois pour affichage
  const grouped = useMemo(() => {
    const prices = Array.isArray(data?.prices) ? data.prices : [];
    return prices.reduce((acc, p) => {
      const d = new Date(p.date);
      const monthLabel = d
        .toLocaleString('fr-FR', { month: 'long', year: 'numeric' })
        .toUpperCase();
      let bucket = acc.find((b) => b.month === monthLabel);
      if (!bucket) {
        bucket = { month: monthLabel, items: [] };
        acc.push(bucket);
      }
      bucket.items.push(p);
      return acc;
    }, []);
  }, [data]);

  if (!isOpen) return null;

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h5 style={{ margin: 0 }}>Choisir une date</h5>
          <button onClick={onClose} style={styles.closeBtn}>
            ×
          </button>
        </div>

        {/* Contexte sélectionné */}
        <div style={{ marginBottom: 8, color: '#6c757d', fontSize: 14 }}>
          {/* <span>Départ: </span>
          <strong>{String(defaultFrom)}</strong>
          <span className="ms-2">•</span> */}
          <span className="ms-2">Durée: </span>
          <strong>
            {Number(nights)} nuit{Number(nights) > 1 ? 's' : ''}
          </strong>
        </div>

        {loading && <p style={styles.muted}>Chargement…</p>}
        {err && <p style={{ color: '#b30000' }}>{String(err)}</p>}

        {!loading && !err && grouped.length === 0 && (
          <p style={styles.muted}>Aucun prix disponible pour cette durée.</p>
        )}

        {!loading &&
          !err &&
          grouped.map(({ month, items }) => (
            <div key={month} style={{ marginBottom: 18 }}>
              <div style={styles.monthBar}>
                <span>{month}</span>
                <span>Prix ttc/pers*</span>
              </div>

              <div>
                {items.map(({ date, price }) => {
                  const d = new Date(date);
                  const startDay = d.toLocaleDateString('fr-FR', { weekday: 'long' });
                  const startNum = d.toLocaleDateString('fr-FR', { day: '2-digit' });

                  // calcule la date de fin en fonction de "nights"
                  let endNum = '—',
                    endDay = '—';
                  const n = Number(nights);
                  if (!Number.isNaN(n) && n > 0) {
                    const end = new Date(d);
                    end.setDate(end.getDate() + n);
                    endDay = end.toLocaleDateString('fr-FR', { weekday: 'long' });
                    endNum = end.toLocaleDateString('fr-FR', { day: '2-digit' });
                  }

                  return (
                    <button
                      key={date}
                      style={styles.row}
                      onClick={() => onSelect({ dateISO: date, price })}
                    >
                      <div style={styles.cellFrom}>
                        <div style={styles.weekday}>{capitalize(startDay)}</div>
                        <div style={styles.daynum}>{startNum}</div>
                      </div>

                      <div style={styles.arrow}>→</div>

                      <div style={styles.cellTo}>
                        <div style={styles.weekday}>{capitalize(endDay)}</div>
                        <div style={styles.daynum}>{endNum}</div>
                      </div>

                      <div style={styles.price}>{Math.round(Number(price))}€</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function capitalize(s = '') {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// mini styles inline (tu peux passer en SCSS/Bootstrap)
const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 12,
  },
  modal: {
    width: 'min(500px, 100%)',
    maxHeight: '85vh',
    overflowY: 'auto',
    borderRadius: 10,
    background: '#fff',
    boxShadow: '0 15px 50px rgba(0,0,0,.3)',
    padding: 16,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: 28,
    lineHeight: 1,
    cursor: 'pointer',
  },
  monthBar: {
    background: '#ff6f80',
    color: '#fff',
    fontWeight: 600,
    padding: '10px 12px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  row: {
    width: '100%',
    marginTop: 10,
    border: '1px solid #e7e7ee',
    borderRadius: 10,
    padding: '14px 16px',
    display: 'grid',
    gridTemplateColumns: '1fr 40px 1fr auto',
    gap: 12,
    alignItems: 'center',
    background: '#fff',
    cursor: 'pointer',
  },
  cellFrom: { textAlign: 'center' },
  cellTo: { textAlign: 'center' },
  weekday: { fontSize: 14, color: '#1a1a4d' },
  daynum: { fontSize: 22, fontWeight: 700, color: '#0a0a3c' },
  arrow: { textAlign: 'center', color: '#888' },
  price: { fontSize: 20, fontWeight: 700, color: '#0a0a3c' },
  muted: { color: '#6c757d' },
};
